// @flow

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { rankWith, uiTypeIs } from '@jsonforms/core';

import useClient from 'common/hooks/useClient';
import {
    Box,
    Flex,
    Stack,
    Position,
    Heading,
    Select,
    useToast,
    Icon
} from '@locus-taxy/neo-ui';
import useLayoutProps from 'platform/settings/form/renderers/util/useLayoutProps';
import { useUpdateStage } from 'platform/settings/hooks/useStage';
import MaraOnlyTag from 'platform/settings/form/renderers/components/MaraOnlyTag';
import { SaveWithReasonButton } from 'platform/settings/form/renderers/components/SaveWithReasonButton';
import { ApiStatusEnum } from 'common/enums/API';

const StageChangeLayout = (props: Props): React.Node => {
    const { t } = useTranslation('platform');
    const client = useClient();
    const updateStage = useUpdateStage();
    const { showToast } = useToast();

    const { uischema } = useLayoutProps(props);
    const [value, setValue] = React.useState({});

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const options = [
        { id: 'TEST', value: 'TEST', label: 'Test' },
        { id: 'DEMO', value: 'DEMO', label: 'Demo' },
        { id: 'DEVO', value: 'DEVO', label: 'Devo' },
        { id: 'PILOT', value: 'PILOT', label: 'Pilot' },
        { id: 'PRE_PROD', value: 'PRE_PROD', label: 'Pre Prod' },
        { id: 'PROD', value: 'PROD', label: 'Prod' },
        { id: 'PROD_PLUS', value: 'PROD_PLUS', label: 'Prod Plus' },
        { id: 'DEMO_INTERNAL', value: 'DEMO_INTERNAL', label: 'Demo Internal' }
    ];

    const filteredOptions = React.useMemo(() => {
        return options.filter(option => option.id !== client.stage);
    }, [options, client]);

    const onSave = (reason: string) => {
        const requestPayload = {
            ...value,
            reason
        };
        updateStage.mutate(requestPayload, {
            onSuccess: () => {
                showToast({
                    title: `Change client stage for ${client.name} is successful.`,
                    palette: 'success',
                    icon: (
                        <Icon
                            icon="check_circle"
                            size="xLarge"
                            color="green.500"
                        />
                    )
                });
            },
            onError: error => {
                showToast({
                    title: error.message,
                    palette: 'danger',
                    icon: (
                        <Icon
                            icon="cross_circle"
                            size="xLarge"
                            color="red.600"
                        />
                    )
                });
            }
        });
    };

    return (
        <Stack space="medium">
            <Heading size="large" color="grey.900">
                {t(uischema.label)}
            </Heading>
            <Box width="100%">
                <Stack space={uischema.options?.space || 'medium'}>
                    <Box>
                        <Select
                            value={value?.stage}
                            onChange={option =>
                                setValue({ stage: option.value })
                            }
                            label={t(
                                'settings.platform.fields.clientSettings.fields.stage.label'
                            )}
                            options={filteredOptions}
                            fullWidth
                        />
                        <Box py="1" ml="auto">
                            <MaraOnlyTag />
                        </Box>
                    </Box>
                    <Box>
                        <Select
                            value={client?.stage}
                            onChange={() => {}}
                            label={t(
                                'settings.platform.fields.clientSettings.fields.stage.currentStage'
                            )}
                            options={options}
                            fullWidth
                            disabled
                        />
                    </Box>
                    <Position
                        position="absolute"
                        bottom="0"
                        right="0"
                        width="100%"
                    >
                        <Flex width="100%" p="4" flexDirection="row-reverse">
                            <SaveWithReasonButton
                                showReasonModal
                                onSave={onSave}
                                isSaving={
                                    updateStage.status === ApiStatusEnum.Loading
                                }
                            />
                        </Flex>
                    </Position>
                </Stack>
            </Box>
        </Stack>
    );
};

export default StageChangeLayout;

export const stageChangeLayoutTester = rankWith(
    1,
    uiTypeIs('StageChangeLayout')
);
